// src/components/padrekko/PadRekkoPanel.jsx
//
// Panel for padrekko mode. Shows selected pad info, footprint, pad
// volumetrics, and access-road curation (detected + user-drawn, with
// include/exclude toggles).

import { useMemo } from "react";
import { X, Layers, Route, Plus, Pencil, Zap, Check, RefreshCcw } from "lucide-react";
import { usePadStore } from "../../stores/usePadStore";
import {
  computeUniformVolumes,
  computeRoadVolumes,
  fmtArea,
  fmtLength,
  fmtVolume,
} from "../../utils/padVolumetrics";
import ReclamationQcTab from "./qc/ReclamationQcTab";
import QcBackToCases from "./qc/QcBackToCases";

/* ---------------------------------------------------------------------- */
/* EMPTY STATE                                                            */
/* ---------------------------------------------------------------------- */

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
      <div className="w-12 h-12 rounded-full bg-slate-800/80 border border-slate-700/50
                      flex items-center justify-center">
        <Layers size={16} className="text-fg-secondary" />
      </div>
      <p className="text-[11px] text-fg-secondary leading-relaxed max-w-[180px]">
        Click a pad on the map to see reclamation volumetrics.
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* HEADER                                                                 */
/* ---------------------------------------------------------------------- */

function PadHeader({ pad, onClear }) {
  return (
    <div className="p-4 border-b border-slate-800/80">
      <div className="flex items-start gap-2.5 mb-3">
        <div className="mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-offset-1
                        ring-offset-slate-900"
          style={{ backgroundColor: "#16f2d4" }} />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold tracking-wide font-mono uppercase"
            style={{ color: "#16f2d4" }}>
            Pad
          </div>
          <div className="text-[13px] font-medium text-white leading-snug mt-0.5 truncate">
            {pad.name}
          </div>
        </div>
        <button
          onClick={onClear}
          className="text-fg-secondary hover:text-slate-300 transition"
          title="Clear selection"
        >
          <X size={12} />
        </button>
      </div>
      <div className="text-[9px] text-fg-secondary font-mono truncate">
        {String(pad.id).slice(0, 24)}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* AREA + VOLUMES                                                         */
/* ---------------------------------------------------------------------- */

function AreaSection({ area }) {
  const f = fmtArea(area);
  return (
    <div className="px-4 py-3 border-b border-slate-800/80">
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-2">
        Footprint
      </div>
      <div className="grid grid-cols-2 gap-x-3">
        <div>
          <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wide">Imperial</div>
          <div className="text-[13px] font-medium text-slate-200 tabular-nums mt-0.5">
            {f.imperial}
          </div>
          <div className="text-[10px] text-fg-secondary tabular-nums">
            {Math.round(area.ft2).toLocaleString()} ft²
          </div>
        </div>
        <div>
          <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wide">Metric</div>
          <div className="text-[13px] font-medium text-slate-200 tabular-nums mt-0.5">
            {f.metric}
          </div>
          <div className="text-[10px] text-fg-secondary tabular-nums">
            {Math.round(area.m2).toLocaleString()} m²
          </div>
        </div>
      </div>
    </div>
  );
}

function VolumesSection({ volumes, title, note }) {
  return (
    <div className="px-4 py-3 border-b border-slate-800/80">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
          {title}
        </div>
        <div className="text-[9px] text-fg-secondary italic">
          Uniform depth
        </div>
      </div>

      <div className="rounded border border-slate-800/60 overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_1fr] text-[9px] uppercase
                        tracking-wide text-fg-secondary bg-slate-800/40 px-2.5 py-1.5
                        border-b border-slate-800/60">
          <div>Depth</div>
          <div className="text-right">Imperial</div>
          <div className="text-right">Metric</div>
        </div>


        {volumes.map((v, i) => {
          const f = fmtVolume(v);
          return (
            <div
              key={v.depthIn}
              className={`grid grid-cols-[auto_1fr_1fr] px-2.5 py-2 text-[11px]
                          tabular-nums items-baseline
                          ${i < volumes.length - 1 ? "border-b border-slate-800/40" : ""}`}
            >
              <div className="text-slate-400 font-medium pr-4">{v.depthIn}"</div>
              <div className="text-right text-slate-200">{f.imperial}</div>
              <div className="text-right text-slate-300">{f.metric}</div>
            </div>
          );
        })}
      </div>

      {note && (
        <div className="text-[9px] text-fg-secondary mt-2 leading-relaxed">
          {note}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* ROAD CHIP                                                              */
/* ---------------------------------------------------------------------- */

function RoadChip({ road, pulse, onToggle, onRemove }) {

  const f = fmtLength(road.length_m);
  const label = road.source === "user"
    ? `Drawn #${road.key.split(":")[1]}`
    : road.roadId?.slice(0, 8) ?? "road";

  // Visual state
  const base = "flex items-center gap-2 rounded border px-2 py-1.5 text-[11px] transition-all";
  const tone = road.included
    ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
    : "bg-slate-800/40 border-slate-700/60 text-fg-secondary";
  const pulseCls = pulse ? "ring-2 ring-amber-400/70 ring-offset-1 ring-offset-slate-900" : "";

  return (
    <div className={`${base} ${tone} ${pulseCls}`}>
      {/* Include toggle */}
      <button
        onClick={() => onToggle(road.key)}
        className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0
                    transition-colors
                    ${road.included
            ? "bg-amber-400 border-amber-400"
            : "bg-transparent border-slate-600 hover:border-slate-400"}`}
        title={road.included ? "Exclude from totals" : "Include in totals"}
      >
        {road.included && <Check size={10} className="text-slate-900" strokeWidth={3} />}
      </button>

      {/* Source badge */}
      <span className="text-[9px] font-mono uppercase tracking-wide opacity-70 shrink-0 flex items-center gap-0.5">
        {road.source === "user"
          ? <><Pencil size={8} /> Drawn</>
          : <><Zap size={8} /> Auto</>}
      </span>

      {/* Identity + length */}
      <span className="font-mono text-[10px] opacity-80 truncate flex-1">
        {label}
      </span>
      <span className="tabular-nums shrink-0 text-[10px]">
        {f.imperial}
      </span>

      {/* Remove (user-drawn only) */}
      {road.source === "user" && (
        <button
          onClick={() => onRemove(road.key)}
          className="text-fg-secondary hover:text-red-400 transition shrink-0"
          title="Delete drawn road"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* ROAD SECTION                                                           */
/* ---------------------------------------------------------------------- */

function RoadsSection({
  roads, loading, error, pulseKey, drawing,
  onToggle, onRemove, onStartDraw, onCancelDraw,
  roadWidthFt, setRoadWidthFt,
}) {

  const included = roads.filter((r) => r.included);
  const totalLength = included.reduce((s, r) => s + r.length_m, 0);
  const roadVols = totalLength > 0 ? computeRoadVolumes(totalLength, roadWidthFt) : null;
  const lengthFmt = fmtLength(totalLength);

  return (
    <div className="px-4 py-3 border-t border-slate-800/80">

      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary
                        flex items-center gap-1.5">
          <Route size={10} />
          Access Roads
        </div>
        <div className="text-[10px] text-fg-secondary">
          {included.length} of {roads.length} included
        </div>
      </div>

      {loading && (
        <div className="text-[11px] text-fg-secondary mb-2">Scanning…</div>
      )}
      {error && (
        <div className="text-[11px] text-red-400/70 mb-2">Failed to load</div>
      )}

      {/* Chip list */}
      {roads.length > 0 && (
        <div className="flex flex-col gap-1 mb-3">
          {roads.map((r) => (
            <RoadChip
              key={r.key}
              road={r}
              pulse={pulseKey === r.key}
              onToggle={onToggle}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}

      {/* Empty detected state */}
      {!loading && roads.length === 0 && (
        <div className="text-[11px] text-fg-secondary italic mb-3">
          No roads detected. Draw one below if needed.
        </div>
      )}

      {/* Draw / cancel action */}
      <button
        onClick={drawing ? onCancelDraw : onStartDraw}
        className={`w-full text-[11px] py-1.5 rounded border transition-colors
                    flex items-center justify-center gap-1.5 mb-3
                    ${drawing
            ? "bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20"
            : "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800 hover:border-slate-600"}`}
      >
        {drawing
          ? <><X size={11} /> Cancel drawing</>
          : <><Plus size={11} /> Draw road</>}
      </button>
      {drawing && (
        <div className="text-[9px] text-fg-secondary italic mb-3 leading-relaxed">
          Click to add points. Right-click or double-click to finish.
        </div>
      )}

      {/* Width input */}
      {included.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
              Assumed width
            </label>
            <div className="flex items-center gap-1.5">
              <input
                type="number" min="1" max="40" step="1"
                value={roadWidthFt}
                onChange={(e) => setRoadWidthFt(e.target.value)}
                className="w-12 px-1.5 py-0.5 text-[11px] bg-slate-800/60
                           border border-slate-700/60 rounded text-slate-200
                           tabular-nums text-right outline-none
                           focus:border-amber-500/40 transition-colors"
              />
              <span className="text-[10px] text-fg-secondary">ft</span>
            </div>
          </div>

          {/* Summary line */}
          <div className="grid grid-cols-2 gap-x-3 mb-3 text-[10px] tabular-nums text-slate-400">
            <div>Total length: <span className="text-slate-200">{lengthFmt.imperial}</span></div>
            <div className="text-right"><span className="text-slate-200">{lengthFmt.metric}</span></div>
          </div>

          {/* Volume table */}
          {roadVols && (
            <div className="rounded border border-slate-800/60 overflow-hidden">
              <div className="grid grid-cols-[auto_1fr_1fr] text-[9px] uppercase
                              tracking-wide text-fg-secondary bg-slate-800/40 px-2.5 py-1
                              border-b border-slate-800/60">
                <div>Depth</div>
                <div className="text-right">Imperial</div>
                <div className="text-right">Metric</div>
              </div>
              {roadVols.volumes.map((v, i) => {
                const f = fmtVolume(v);
                return (
                  <div
                    key={v.depthIn}
                    className={`grid grid-cols-[auto_1fr_1fr] px-2.5 py-1.5 text-[10px]
                                tabular-nums items-baseline
                                ${i < roadVols.volumes.length - 1 ? "border-b border-slate-800/40" : ""}`}
                  >
                    <div className="text-slate-400 font-medium pr-4">{v.depthIn}"</div>
                    <div className="text-right text-slate-200">{f.imperial}</div>
                    <div className="text-right text-slate-300">{f.metric}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-[9px] text-fg-secondary mt-2 leading-relaxed">
            Road volumes include only checked roads. Separate from pad totals.
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* MAIN                                                                   */
/* ---------------------------------------------------------------------- */

// Tab metadata for the panel-level strip. Order matters — first entry is the
// default. Adding more tabs later means a row here + a branch in the body
// switch; no other plumbing.
const PANEL_TABS = [
  { id: "qc",          label: "Reclamation QC" },
  { id: "volumetrics", label: "Volumetrics" },
];

export default function PadRekkoPanel() {
  // Tab state lives in usePadStore so MapPage can gate the full-bleed QC
  // project dashboard on it (and the dashboard can switch back to Volumetrics).
  // Cross-tab pad clicks still update usePadStore.selectedPad silently; the
  // active tab is unaffected (clicking a pad while on the QC tab won't snap
  // you to Volumetrics — setPadrekkoTab is the only writer).
  const activeTab = usePadStore(s => s.padrekkoTab);
  const setActiveTab = usePadStore(s => s.setPadrekkoTab);

  const selectedPad = usePadStore(s => s.selectedPad);
  const clearSel = usePadStore(s => s.clearSelection);

  const roadSelections = usePadStore(s => s.roadSelections);
  const loadingRoads = usePadStore(s => s.loadingRoads);
  const roadsError = usePadStore(s => s.roadsError);
  const pulseRoadKey = usePadStore(s => s.pulseRoadKey);
  const drawingRoad = usePadStore(s => s.drawingRoad);

  const toggleRoad = usePadStore(s => s.toggleRoadInclusion);
  const removeUserRoad = usePadStore(s => s.removeUserRoad);
  const setDrawingRoad = usePadStore(s => s.setDrawingRoad);

  const roadWidthFt = usePadStore(s => s.roadWidthFt);
  const setRoadWidthFt = usePadStore(s => s.setRoadWidthFt);

  const result = useMemo(
    () => (selectedPad?.geom ? computeUniformVolumes(selectedPad.geom) : null),
    [selectedPad?.geom]
  );

  return (
    <div className="h-full w-full bg-slate-900 border-r border-slate-800/80 flex flex-col">

      {/* Header */}
      <div className="p-3 border-b border-slate-800 flex items-center justify-between shrink-0" data-padrekko-panel-header>
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
            <RefreshCcw size={12} />
            PadRekko
          </div>
          <div className="text-[10px] text-fg-secondary mt-0.5">
            Pad reclamation tools
          </div>
        </div>
        {/* Leave the open QC case. QC tab only; renders null with no case. */}
        {activeTab === "qc" && <QcBackToCases />}
      </div>

      {/* Tab strip — panel-level. Each tab body owns its own content gates
          (Volumetrics keeps the existing selectedPad gate; QC has none). */}
      <div className="flex border-b border-slate-800/80 shrink-0" role="tablist">
        {PANEL_TABS.map((t) => {
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 px-3 py-2 text-[11px] font-medium uppercase
                          tracking-wide border-b-2 transition
                          ${isActive
                            ? "text-white"
                            : "text-fg-secondary hover:text-slate-300 border-transparent"}`}
              style={isActive ? { borderBottomColor: "#16f2d4" } : undefined}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body — one branch per tab. Volumetrics is the existing flow,
          byte-for-byte unchanged inside the wrapper. QC is a placeholder
          until the next change. */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === "volumetrics" && (
          <>
            {!selectedPad && <EmptyState />}

            {selectedPad && !result && (
              <div className="p-4 text-[11px] text-fg-secondary">
                Computing volumetrics…
              </div>
            )}

            {selectedPad && result && (
              <>
                <PadHeader pad={selectedPad} onClear={clearSel} />
                <AreaSection area={result.area} />
                <VolumesSection
                  title="Pad Reclamation Volume"
                  volumes={result.volumes}
                  note="Volume = footprint area × depth. Assumes flat reclamation surface."
                />
                <RoadsSection
                  roads={roadSelections}
                  loading={loadingRoads}
                  error={roadsError}
                  pulseKey={pulseRoadKey}
                  drawing={drawingRoad}
                  onToggle={toggleRoad}
                  onRemove={removeUserRoad}
                  onStartDraw={() => setDrawingRoad(true)}
                  onCancelDraw={() => setDrawingRoad(false)}
                  roadWidthFt={roadWidthFt}
                  setRoadWidthFt={setRoadWidthFt}
                />
              </>
            )}
          </>
        )}

        {activeTab === "qc" && <ReclamationQcTab />}
      </div>
    </div>
  );
}
